import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Polygon, FeatureGroup, Marker, Popup, useMap } from "react-leaflet";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "leaflet/dist/leaflet.css";
import "./Styles.css";
import useGeoLocation from "../utils/useGeoLocation";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import MarkerObject from "./MarkerMap";
import LeafletGeoCoder from "./LeafletGeoCoder";
import SensorClusterGroup from "../components/maps/SensorClusterGroup";
import icon from "../assets/images/icons/icon.png";
import sensor from "../assets/images/positioning.png";
import positioning from "../assets/images/positioning.png";
import farmImage from "../assets/images/farm.png";
import defaultLeafletMarkerIcon from "../utils/leafletConfig";

const zoomDefault = 14;
const centerDefault = [36.806389, 10.181667];

const myIcon = new L.Icon({
  iconUrl: icon,
  iconSize: [40, 45],
  iconAnchor: [17, 45],
  popupAnchor: [3, -46],
});

const Iconsensor = new L.Icon({
  iconUrl: sensor,
  iconSize: [40, 42],
  iconAnchor: [17, 45],
  popupAnchor: [3, -46],
});

const NoSensorIcon = new L.Icon({
  iconUrl: positioning,
  iconSize: [40, 42],
  iconAnchor: [17, 45],
  popupAnchor: [3, -46],
});

const FarmIcon = L.divIcon({
  className: "farm-marker-div-icon",
  html: `<img src="${farmImage}" alt="" width="40" height="42" style="display:block;object-fit:contain" onerror="this.onerror=null;this.src='${positioning}';" />`,
  iconSize: [40, 42],
  iconAnchor: [17, 45],
  popupAnchor: [3, -46],
});

const SetViewOnClick = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !center) return;
    map.whenReady(() => {
      // double garde: carte chargée + container monté
      if (map._loaded && map._container && map._container.offsetWidth > 0) {
        try {
          map.setView(center, zoom, { animate: false });
        } catch (e) {
          console.warn("Safe setView skipped:", e);
        }
      }
    });
  }, [map, center, zoom]);
  return null;
};

const LocateControl = ({ onLocationSelected }) => {
  const map = useMap();
  const locationMarkerRef = useRef(null);

  useEffect(() => {
    const control = L.control({ position: "topleft" });

    control.onAdd = () => {
      const container = L.DomUtil.create("div", "leaflet-bar sensor-locate-control");
      const button = L.DomUtil.create("button", "sensor-locate-button", container);
      const notice = L.DomUtil.create("span", "sensor-locate-notice", container);
      button.type = "button";
      button.title = "Utiliser ma position";
      button.setAttribute("aria-label", "Utiliser ma position");
      button.innerHTML = '<i class="fas fa-location-crosshairs" aria-hidden="true"></i>';
      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      const showNotice = (text) => {
        notice.textContent = text;
        notice.classList.add("is-visible");
        window.setTimeout(() => notice.classList.remove("is-visible"), 4500);
      };

      L.DomEvent.on(button, "click", (event) => {
        L.DomEvent.stop(event);
        if (!("geolocation" in navigator)) {
          showNotice("Géolocalisation indisponible");
          return;
        }

        button.classList.add("is-loading");
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const latLng = [lat, lng];
            map.flyTo(latLng, 16, { animate: true });

            if (locationMarkerRef.current) {
              locationMarkerRef.current.setLatLng(latLng);
            } else {
              locationMarkerRef.current = L.marker(latLng, { icon: defaultLeafletMarkerIcon })
                .addTo(map)
                .bindPopup("Position du capteur");
            }

            locationMarkerRef.current.openPopup();
            onLocationSelected?.({ lat, lng });
            button.classList.remove("is-loading");
          },
          () => {
            button.classList.remove("is-loading");
            showNotice("Autorisez la localisation dans le navigateur");
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });

      return container;
    };

    control.addTo(map);
    return () => {
      map.removeControl(control);
      if (locationMarkerRef.current) {
        locationMarkerRef.current.remove();
        locationMarkerRef.current = null;
      }
    };
  }, [map, onLocationSelected]);

  return null;
};

// Independent from route; we include it only where needed
const FitBoundsToSensors = ({ sensors }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !Array.isArray(sensors) || sensors.length === 0) return;
    const valid = sensors.filter((s) => s?.Latitude && s?.Longitude);
    if (valid.length === 0) return;

    if (valid.length === 1) {
      const s = valid[0];
      map.whenReady(() =>
        map.setView([Number(s.Latitude), Number(s.Longitude)], 16)
      );
    } else {
      const bounds = L.latLngBounds(
        valid.map((s) => [Number(s.Latitude), Number(s.Longitude)])
      );
      map.whenReady(() => map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15 }));
    }
  }, [map, sensors]);
  return null;
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !Array.isArray(points) || points.length === 0) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [map, points]);
  return null;
};

const LeafletMap = ({
  type,
  data = [],
  _onCreated = () => {},
  _onEdited = () => {},
  draw = {},
  edit = {},
  sensor = [],
  farms = [],
  fields = [],
  zoom,
  center,
  fromAction,
  uid,
  enableAutoLocation = true,
  showLocationControl = false,
  onLocationSelected,
}) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const locationHook = useGeoLocation(enableAutoLocation);
  const route = useLocation();
  const currentPath = route.pathname;

  const sensorFieldsPoints = [];
  const noSensorFieldsPoints = [];

  const [mapCenter, setMapCenter] = useState(centerDefault);
  const [zoomLevel, setZoomLevel] = useState(10);
  const mapDrawOptions = {
    ...draw,
    marker: draw.marker
      ? {
          icon: defaultLeafletMarkerIcon,
          ...(typeof draw.marker === "object" ? draw.marker : {}),
        }
      : false,
  };

  const getCenterFromSensors = useCallback(() => {
    if (sensor && sensor.length > 0) {
      const firstSensor = sensor.find((s) => s?.Latitude && s?.Longitude);
      if (firstSensor) {
        return [Number(firstSensor.Latitude), Number(firstSensor.Longitude)];
      }
    }
    return null;
  }, [sensor]);

  // Avoid auto-center for Dashboard-supplier; we fit via FitBoundsToSensors
  useEffect(() => {
    if (currentPath === "/Dashboard-supplier") return;
    const centerFromSensors = getCenterFromSensors();
    if (centerFromSensors) {
      setMapCenter(centerFromSensors);
      setZoomLevel(16.5);
    } else {
      setMapCenter(centerDefault);
      setZoomLevel(10);
    }
  }, [currentPath, getCenterFromSensors]);

  // Invalidate size on route/sensor changes to prevent blank tiles
useEffect(() => {
  if (!mapRef.current) return;
  
  // Utiliser un délai plus long pour s'assurer que la carte est complètement montée
  const t = setTimeout(() => {
    if (mapRef.current && typeof mapRef.current.invalidateSize === 'function') {
      mapRef.current.invalidateSize();
    }
  }, 300);
  
  return () => clearTimeout(t);
}, [currentPath, sensor?.length]);

  const getCentroid = (coords) => {
    if (!Array.isArray(coords) || coords.length === 0) return centerDefault;
    const latSum = coords.reduce((sum, c) => sum + c[0], 0);
    const lngSum = coords.reduce((sum, c) => sum + c[1], 0);
    return [latSum / coords.length, lngSum / coords.length];
  };

  const returnedMap = (L) => {
    switch (currentPath) {
      case "/AddSensor":
        if (!farms || !Array.isArray(farms)) return null;
        return farms.map((item, indx) => {
          let coordinates = [];
          const coord = JSON.parse(item.coordinates || "null");
          if (coord) {
            coord.forEach((co) => {
              coordinates.push(Object.values(co));
            });
          }
          if (!coord) return null;
          return (
            <React.Fragment key={indx}>
              <Polygon positions={coordinates} />
              {Array.isArray(data) &&
                data.map((it, i) => {
                  if (it?.Latitude && it?.Longitude) {
                    return (
                      <MarkerObject
                        key={i}
                        lat={it.Latitude}
                        long={it.Longitude}
                        name={it.code}
                        id={it.id}
                      />
                    );
                  }
                  return null;
                })}
            </React.Fragment>
          );
        });

      case "/":
        if (!data || !Array.isArray(data)) return null;
        const mapMarkers = [];
        const fieldLayers = data.flatMap((item, farmIdx) => {
          const flds = item.fields || [];
          return flds.map((field, fieldIdx) => {
                let fieldCoordinates = [];
                let hasMappableSensor = false;

                if (field?.coordinates) {
                  try {
                    const coord = JSON.parse(field.coordinates);
                    if (Array.isArray(coord) && coord.length > 0) {
                      fieldCoordinates = coord.map((co) => [co.Lat, co.Long]);
                      if (field?.sensors?.length > 0) {
                        sensorFieldsPoints.push(...fieldCoordinates);
                      } else {
                        noSensorFieldsPoints.push(...fieldCoordinates);
                      }
                    }
                  } catch (e) {
                    console.error("Invalid field coordinates", e);
                  }
                }

                if (field?.sensors?.length > 0) {
                  field.sensors.forEach((s, sensorIdx) => {
                    if (s?.Latitude && s?.Longitude) {
                      hasMappableSensor = true;
                      sensorFieldsPoints.push([s.Latitude, s.Longitude]);
                      mapMarkers.push(
                        <Marker
                          key={`sensor-${s.id || s.uid || `${farmIdx}-${fieldIdx}-${sensorIdx}`}`}
                          icon={Iconsensor}
                          position={[s.Latitude, s.Longitude]}
                        >
                          <Popup>{s.code}</Popup>
                        </Marker>
                      );
                    }
                  });
                }

                if (!hasMappableSensor && fieldCoordinates.length > 0) {
                  mapMarkers.push(
                    <Marker
                      key={`farm-${field.id || field.uid || `${farmIdx}-${fieldIdx}`}`}
                      icon={FarmIcon}
                      position={getCentroid(fieldCoordinates)}
                    >
                      <Popup>{t("map_no_sensors_in_field", { defaultValue: "No Sensors in this field" })}</Popup>
                    </Marker>
                  );
                }

                return (
                  <React.Fragment key={`${farmIdx}-${fieldIdx}`}>
                    {fieldCoordinates.length > 2 && (
                      <Polygon
                        pathOptions={{ color: "#28A6B7", weight: 2, fillOpacity: 0.5 }}
                        positions={fieldCoordinates}
                      />
                    )}

                  </React.Fragment>
                );
              });
        });
        return (
          <>
            {fieldLayers}
            {mapMarkers.length > 0 && (
              <SensorClusterGroup>{mapMarkers}</SensorClusterGroup>
            )}
          </>
        );

      case "/Dashboard-supplier":
        if (!Array.isArray(sensor) || sensor.length === 0) {
          return (
            <Marker icon={NoSensorIcon} position={centerDefault}>
              <Popup>
                <div style={{ textAlign: "center", padding: 5 }}>
                  <strong>{t("map_no_sensor_available", { defaultValue: "Aucun capteur disponible" })}</strong>
                  <p>{t("map_add_sensors_to_see_them", { defaultValue: "Ajoutez des capteurs pour les voir sur la carte" })}</p>
                </div>
              </Popup>
            </Marker>
          );
        }
        return (
          <>
            {sensor.map((s, i) => {
              if (!s?.Latitude || !s?.Longitude) return null;
              return (
                <MarkerObject
                  key={`sensor-${s.id || i}`}
                  lat={s.Latitude}
                  long={s.Longitude}
                  name={s.code || `Capteur ${i + 1}`}
                  id={s.id || `sensor-${i}`}
                />
              );
            })}
          </>
        );

      case `/admin/user/${uid}/farms`:
        if (!data || !Array.isArray(data)) return null;
        return data.map((it, i) => {
          let coordinates = [];
          const coord = JSON.parse(it.coordinates || "null");
          if (coord) {
            coord.forEach((co) => coordinates.push(Object.values(co)));
          }
          if (coord) {
            return (
              <Polygon key={i} positions={coordinates}>
                <MarkerObject lat={it.Latitude} long={it.Longitude} name={it.name} id={it.id} />
              </Polygon>
            );
          }
          return (
            <MarkerObject
              key={i}
              lat={it.Latitude}
              long={it.Longitude}
              name={it.name}
              id={it.id}
            />
          );
        });

      case `/admin/user/${uid}/fields`:
        if (!data || !Array.isArray(data)) return null;
        return data.map((it, i) => {
          let coordinates = [];
          const coord = JSON.parse(it.coordinates || "null");
          if (coord) {
            coord.forEach((co) => coordinates.push(Object.values(co)));
          }
          if (!coord) return null;
          return (
            <React.Fragment key={i}>
              <Polygon positions={coordinates} />
              {fields &&
                fields.map((f, idx) => {
                  if (f?.Latitude) {
                    return (
                      <MarkerObject
                        key={idx}
                        lat={f.Latitude}
                        long={f.Longitude}
                        name={f.title}
                        id={f.id}
                      />
                    );
                  }
                  return null;
                })}
            </React.Fragment>
          );
        });

      case `/admin/user/${uid}/sensors`:
        if (!farms || !Array.isArray(farms)) return null;
        return farms.map((fm, i) => {
          let coordinates = [];
          const coord = JSON.parse(fm.coordinates || "null");
          if (coord) {
            coord.forEach((co) => coordinates.push(Object.values(co)));
          }
          if (!coord) return null;
          return (
            <React.Fragment key={i}>
              <Polygon positions={coordinates} />
              {Array.isArray(data) &&
                data.map((it, idx) => {
                  if (it?.Latitude && it?.Longitude) {
                    return (
                      <MarkerObject
                        key={idx}
                        lat={it.Latitude}
                        long={it.Longitude}
                        name={it.code}
                        id={it.id}
                      />
                    );
                  }
                  return null;
                })}
            </React.Fragment>
          );
        });

      default:
        return null;
    }
  };

  const initialCenter = useMemo(() => {
    if (currentPath === "/Dashboard-supplier" && Array.isArray(sensor) && sensor.length > 0) {
      const first = sensor.find((s) => s?.Latitude && s?.Longitude);
      if (first) return [Number(first.Latitude), Number(first.Longitude)];
    }
    return mapCenter;
  }, [currentPath, sensor, mapCenter]);

  const initialZoom = useMemo(() => {
    if (currentPath === "/Dashboard-supplier" && Array.isArray(sensor) && sensor.length > 0) {
      return sensor.length === 1 ? 16 : 12;
    }
    return zoom || zoomLevel;
  }, [currentPath, sensor, zoom, zoomLevel]);

  return (
     <div>
      <MapContainer
        // AVANT :
        // key={`map-${currentPath}-${sensor?.length || 0}`}
        // APRÈS : clé supprimée pour éviter les démontages intempestifs pendant les animations
        id="main-leaflet-map"
        style={{ borderRadius: 20, boxShadow: "1px 1px 10px #bbb", height: 300, zIndex: 1 }}
        className="markercluster-map"
        center={initialCenter}
        zoom={initialZoom}
        maxZoom={18}
        whenCreated={(map) => {
          mapRef.current = map;
        }}
      >
        {currentPath !== "/Dashboard-supplier" && (
          <FeatureGroup>
            <EditControl
              draw={mapDrawOptions}
              edit={edit}
              position="topright"
              onCreated={_onCreated}
              onEdited={_onEdited}
            />
          </FeatureGroup>
        )}

        <TileLayer
          url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
          subdomains={["mt0", "mt1", "mt2", "mt3"]}
          attribution="© Google Maps"
        />

        {showLocationControl && <LocateControl onLocationSelected={onLocationSelected} />}

        {returnedMap(L)}

        {currentPath === "/Dashboard-supplier" && <FitBoundsToSensors sensors={sensor} />}

        {currentPath !== "/Dashboard-supplier" && (
          <FitBounds
            points={sensorFieldsPoints.length > 0 ? sensorFieldsPoints : noSensorFieldsPoints}
          />
        )}

        {currentPath !== "/Dashboard-supplier" && <LeafletGeoCoder />}

        {locationHook.loaded && !locationHook.error && (
          <Marker icon={myIcon} position={[locationHook.coordinates.lat, locationHook.coordinates.lng]}>
            <Popup>{t("map_my_position", { defaultValue: "My position" })}</Popup>
          </Marker>
        )}

        {fromAction && (
          <SetViewOnClick center={center?.length ? center : centerDefault} zoom={zoom || zoomDefault} />
        )}
      </MapContainer>
    </div>
  );
};

export default LeafletMap;
